import { useCallback, useEffect, useState } from "react";
import { getJson, AuthError } from "../lib/auth";
import { Card, Spinner, ErrorBanner } from "../components";
import { theme, fontStack, radius } from "../theme";

/**
 * LA PAGE D'ACCUEIL de la console (demande d'Henri, 3 août) : le travail d'un
 * modérateur, ce sont les DOSSIERS — pas des courbes de fréquentation.
 *
 * Deuxième principe, hérité du même retour (« l'interface est trop compliquée ») :
 * cette liste parle FRANÇAIS. Ni `REPORT_CONTENT`, ni `RECEIVED`, ni compteur
 * d'« événements » — un modérateur veut savoir QUI, QUOI, DEPUIS QUAND, et
 * s'il y a quelque chose de neuf.
 */
interface ReportRow {
  id: string;
  reference?: string | null;
  type?: string;
  status?: string;
  productStatus?: string;
  reason?: string | null;
  hasVault?: boolean;
  createdAt?: string;
  /** MOD-6 — une réponse d'utilisateur attend d'être lue. */
  adminUnread?: boolean;
  reportedUser?: { displayName?: string | null } | null;
  [k: string]: unknown;
}

/** Filtres exprimés comme un modérateur les pense, pas comme la base les stocke. */
const VIEWS = [
  { key: "OPEN", label: "À traiter" },
  { key: "REVIEWED", label: "En cours" },
  { key: "ALL", label: "Tous" },
  { key: "ACTIONED", label: "Sanctionnés" },
  { key: "DISMISSED", label: "Classés" },
] as const;

const TYPES = [
  { key: "ALL", label: "Tout" },
  { key: "REPORT_CONTENT", label: "Signalements" },
  { key: "BUG", label: "Bugs" },
  { key: "FEATURE", label: "Suggestions" },
  { key: "SUPPORT", label: "Demandes d'aide" },
] as const;

const TYPE_LABEL: Record<string, string> = {
  REPORT_CONTENT: "Signalement",
  BUG: "Bug",
  FEATURE: "Suggestion",
  SUPPORT: "Demande d'aide",
};

/** Statut → phrase lisible + couleur. Aucun code technique à l'écran. */
function statusView(s?: string): { label: string; color: string; bg: string } {
  switch (s) {
    case "RECEIVED":
    case "OPEN":
      return { label: "À traiter", color: theme.warning, bg: "rgba(232,161,58,0.14)" };
    case "IN_REVIEW":
    case "REVIEWED":
      return { label: "En cours d'analyse", color: theme.ink, bg: theme.surface3 };
    case "NEEDS_INFO":
      return { label: "Complément demandé", color: theme.warning, bg: "rgba(232,161,58,0.14)" };
    case "ACTIONED":
      return { label: "Sanction prise", color: theme.accent, bg: "rgba(200,255,100,0.14)" };
    case "DISMISSED":
      return { label: "Aucune sanction", color: theme.ink2, bg: theme.surface2 };
    case "RESOLVED":
      return { label: "Traité", color: theme.accent, bg: "rgba(200,255,100,0.14)" };
    case "PLANNED":
      return { label: "Planifié", color: theme.ink2, bg: theme.surface2 };
    case "DECLINED":
      return { label: "Refusé", color: theme.ink2, bg: theme.surface2 };
    default:
      return { label: s ?? "—", color: theme.ink3, bg: theme.surface2 };
  }
}

/** « il y a 3 jours » plutôt qu'une date à décoder : l'ancienneté est ce qui compte. */
function since(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  return j === 1 ? "hier" : `il y a ${j} jours`;
}

export function ReportsInbox({
  onOpen,
  onAuthLost,
}: {
  onOpen: (id: string) => void;
  onAuthLost: () => void;
}) {
  const [status, setStatus] = useState<string>("OPEN");
  const [type, setType] = useState<string>("ALL");
  const [rows, setRows] = useState<ReportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setRows(null);
    setError(null);
    const qs = new URLSearchParams();
    if (status !== "ALL") qs.set("status", status);
    if (type !== "ALL") qs.set("type", type);
    const q = qs.toString();
    getJson<ReportRow[]>(`/v1/admin/reports${q ? `?${q}` : ""}`)
      .then(setRows)
      .catch((e) => {
        if (e instanceof AuthError && e.status === 401) return onAuthLost();
        setError(e instanceof Error ? e.message : "Erreur");
      });
  }, [status, type, onAuthLost]);

  useEffect(load, [load]);

  const unreadCount = (rows ?? []).filter((r) => r.adminUnread).length;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
        <h1 style={{ fontFamily: fontStack.uiSemiBold, fontSize: 24 }}>Dossiers</h1>
        {unreadCount > 0 && (
          <span style={{ color: theme.accent, fontSize: 14, fontFamily: fontStack.uiSemiBold }}>
            {unreadCount} nouveau{unreadCount > 1 ? "x" : ""} message{unreadCount > 1 ? "s" : ""}
          </span>
        )}
      </div>
      <p style={{ color: theme.ink3, fontSize: 13.5, marginBottom: 18 }}>
        Les plus anciens d'abord : c'est le temps d'attente qui compte.
      </p>

      {/* Onglets plutôt que menus déroulants : un clic, pas deux. */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        {VIEWS.map((v) => (
          <Chip key={v.key} active={status === v.key} onClick={() => setStatus(v.key)}>
            {v.label}
          </Chip>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" }}>
        {TYPES.map((t) => (
          <Chip key={t.key} active={type === t.key} onClick={() => setType(t.key)} subtle>
            {t.label}
          </Chip>
        ))}
      </div>

      {error && <ErrorBanner message={error} />}
      {!rows && !error && <Spinner label="Chargement des dossiers…" />}

      {rows && rows.length === 0 && (
        <Card>
          <div style={{ color: theme.ink3, padding: "8px 0" }}>
            Rien à traiter ici. Bonne nouvelle.
          </div>
        </Card>
      )}

      {/* Des CARTES, pas un tableau : chaque dossier est une décision à prendre,
          pas une ligne de base de données. */}
      {rows && rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((r) => {
            const st = statusView(r.productStatus || r.status);
            return (
              <button
                key={r.id}
                onClick={() => onOpen(r.id)}
                style={{
                  textAlign: "left",
                  background: theme.surface,
                  border: `1px solid ${r.adminUnread ? theme.accent : theme.line}`,
                  borderRadius: radius.sheet,
                  padding: "14px 16px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
                    <span
                      style={{
                        background: st.bg,
                        color: st.color,
                        borderRadius: radius.pill,
                        padding: "2px 10px",
                        fontSize: 12,
                        fontFamily: fontStack.uiSemiBold,
                      }}
                    >
                      {st.label}
                    </span>
                    <span style={{ color: theme.ink2, fontSize: 13 }}>
                      {TYPE_LABEL[r.type ?? ""] ?? r.type}
                    </span>
                    {r.adminUnread && (
                      <span
                        style={{
                          background: theme.accent,
                          color: theme.onAccent,
                          borderRadius: radius.pill,
                          padding: "2px 9px",
                          fontSize: 11.5,
                          fontFamily: fontStack.uiSemiBold,
                          fontWeight: 700,
                        }}
                      >
                        Nouveau message
                      </span>
                    )}
                    {r.hasVault && (
                      <span style={{ color: theme.ink3, fontSize: 12 }} title="Conversation mise de côté comme preuve">
                        · preuves
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      fontSize: 14.5,
                      color: theme.ink,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.reason || "Sans message"}
                  </div>

                  <div style={{ color: theme.ink3, fontSize: 12, marginTop: 4 }}>
                    {r.reportedUser?.displayName ? `Vise ${r.reportedUser.displayName} · ` : ""}
                    {since(r.createdAt)} · {r.reference || r.id.slice(0, 8)}
                  </div>
                </div>
                <span style={{ color: theme.ink3, fontSize: 20 }} aria-hidden>
                  ›
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
  subtle,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  subtle?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? (subtle ? theme.surface3 : theme.accent) : "transparent",
        color: active ? (subtle ? theme.ink : theme.onAccent) : theme.ink3,
        border: `1px solid ${active ? (subtle ? theme.accent : theme.accent) : theme.line}`,
        borderRadius: radius.pill,
        padding: subtle ? "5px 12px" : "7px 14px",
        fontSize: subtle ? 12.5 : 13.5,
        fontFamily: active ? fontStack.uiSemiBold : fontStack.ui,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
